/**
 * The message editor inside the campaign wizard.
 *
 * Email is edited visually: the box is contenteditable, so bold reads as bold
 * while you type rather than as a tag you have to picture. HTML mode shows the
 * same content as source for anyone who wants to hand-tune it.
 *
 * The other channels are deliberately not visual, because their formatting is
 * not HTML and pretending otherwise would produce markers they cannot carry:
 * WhatsApp renders its own *bold* / _italic_ / ~strike~ markers, and SMS and
 * Push carry no formatting at all.
 *
 * Whatever is written here, the send console flattens HTML to text before a
 * mailto: hand-off, because that is all a mail client will accept from a link.
 */
import { useEffect, useRef, useState } from 'react';
import Icon from '../../lib/icons.jsx';
import api from '../../lib/api.js';
import { fileToBase64 } from '../../lib/format.js';
import { isOffline } from '../../lib/storage.js';
import { Spinner } from '../Primitives.jsx';
import { useInlineImages, seedInlineImage } from '../../lib/inlineImages.js';
import { storableBody } from '../../domain/marketing.js';
import { MERGE, LEDGER_MERGE } from '../../constants/marketing.js';

export const looksHtml = text => /<\/?[a-z][\s\S]*>/i.test(String(text || ''));

/** Flatten HTML to the text a mailto: or wa.me hand-off can actually carry. */
export const htmlToText = html => String(html || '')
  .replace(/<\s*br\s*\/?\s*>/gi, '\n')
  .replace(/<\s*\/\s*(p|div|li|h[1-6])\s*>/gi, '\n')
  .replace(/<\s*li[^>]*>/gi, '• ')
  .replace(/<[^>]+>/g, '')
  .replace(/&nbsp;/gi, ' ')
  .replace(/&amp;/gi, '&')
  .replace(/&lt;/gi, '<')
  .replace(/&gt;/gi, '>')
  .replace(/\n{3,}/g, '\n\n')
  .trim();

export default function CampaignEditor({ channel, html, onHtml, body, onBody, signature, onNote }) {
  const imageRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const areaRef = useRef(null);
  const liveRef = useRef(null);
  const emitted = useRef('');

  const isEmail = channel === 'email';
  const isWhatsapp = channel === 'whatsapp';
  const visual = isEmail && !html;

  /*
   * A contenteditable cannot be driven like a controlled input — writing the
   * value back on every keystroke would drop the caret to the start. So it is
   * only refilled when the change came from somewhere other than typing here.
   */
  /* What the author sees: the same copy with the pictures drawn in. */
  const shown = useInlineImages(body);

  useEffect(() => {
    const el = liveRef.current;
    if (!el || !visual) return;
    if (shown !== emitted.current && el.innerHTML !== shown) el.innerHTML = shown;
  }, [shown, visual]);

  const publish = () => {
    const el = liveRef.current;
    if (!el) return;
    emitted.current = el.innerHTML;
    onBody(storableBody(el.innerHTML));
  };

  /** Apply a formatting command to the selection, live. */
  const exec = (command, value) => {
    const el = liveRef.current;
    if (!el || typeof document.execCommand !== 'function') return;
    el.focus();
    document.execCommand(command, false, value);
    publish();
  };

  /** The plain-text channels wrap the selection in their own markers instead. */
  const wrap = (before, after) => {
    const el = areaRef.current;
    const current = String(body || '');
    if (!el) { onBody(current + before + after); return; }
    const from = el.selectionStart ?? 0;
    const to = el.selectionEnd ?? 0;
    const picked = current.slice(from, to) || 'text';
    onBody(current.slice(0, from) + before + picked + after + current.slice(to));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(from + before.length, from + before.length + picked.length);
    });
  };

  const askLink = () => {
    // eslint-disable-next-line no-alert
    const url = window.prompt('Link address', 'https://');
    return url ? String(url).trim() : '';
  };

  /* ------------------------------------------------------------ insertions */

  /** Put markup at the caret in the visual box, or at the end in source mode. */
  const insert = markup => {
    if (visual) {
      const el = liveRef.current;
      if (el && typeof document.execCommand === 'function') {
        el.focus();
        document.execCommand('insertHTML', false, markup);
        publish();
        return;
      }
    }
    onBody(String(body || '') + markup);
  };

  /**
   * A picture from the machine, stored once and sent inside the message.
   *
   * Not a data: URI — that inflates every copy sent and Gmail and Outlook both
   * refuse to render one. Not a hosted link either, because that needs an
   * address the recipient's mail app can reach and a workspace on a private
   * network has none. The server keeps the file and the message carries it as
   * an inline attachment, which is the one form that arrives either way.
   *
   * The editor shows the data URL the upload returns; the body carries the
   * `cid:` the message will use.
   */
  const addImage = async file => {
    if (!file) return;
    if (isOffline()) { if (onNote) onNote('Adding an image needs the server'); return; }

    setUploading(true);
    try {
      const asset = await api.marketing.asset({
        name: file.name,
        mime: file.type,
        data: await fileToBase64(file)
      });

      seedInlineImage(asset.cid, asset.preview);

      insert(
        `<img src="${visual ? asset.preview : `cid:${asset.cid}`}" data-cid="${asset.cid}" `
        + `alt="${String(asset.name).replace(/"/g, '&quot;')}" `
        + 'style="max-width:100%;height:auto;border-radius:8px" />');
    } catch (e) {
      if (onNote) onNote(e.message);
    }
    setUploading(false);
  };

  /** The signature from Settings → Profile, as its own block. */
  const addSignature = () => {
    const text = String(signature || '').trim();
    if (!text) return;

    if (isEmail) {
      /*
       * A rule above it, and quieter than the message.
       *
       * Without those a one-line signature reads as another sentence of the
       * email, which is exactly what it looked like. The separation is what
       * makes a signature a signature; the words on their own do not.
       */
      insert(
        '<p style="margin-top:26px;padding-top:14px;border-top:1px solid #e6e8ee;'
        + 'color:#6b7280;font-size:13px;line-height:1.6">'
        + text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/\n/g, '<br />')
        + '</p>');
      return;
    }
    onBody(`${String(body || '').replace(/\s+$/, '')}\n\n${text}`);
  };

  /* One toolbar, three dialects: live commands, raw tags, or WhatsApp markers. */
  const tools = visual
    ? [
      ['B', 'Bold', () => exec('bold')],
      ['I', 'Italic', () => exec('italic')],
      ['U', 'Underline', () => exec('underline')],
      ['Link', 'Link', () => { const u = askLink(); if (u) exec('createLink', u); }],
      ['•', 'Bulleted list', () => exec('insertUnorderedList')],
      ['1.', 'Numbered list', () => exec('insertOrderedList')],
      ['✕', 'Strip formatting', () => exec('removeFormat')]
    ]
    : isEmail
      ? [
        ['B', 'Bold', () => wrap('<b>', '</b>')],
        ['I', 'Italic', () => wrap('<i>', '</i>')],
        ['U', 'Underline', () => wrap('<u>', '</u>')],
        ['Link', 'Link', () => { const u = askLink(); if (u) wrap(`<a href="${u}">`, '</a>'); }],
        ['•', 'Bulleted list', () => wrap('\n<ul>\n  <li>', '</li>\n</ul>\n')],
        ['1.', 'Numbered list', () => wrap('\n<ol>\n  <li>', '</li>\n</ol>\n')],
        ['✕', 'Strip formatting', () => onBody(htmlToText(body))]
      ]
      : isWhatsapp
        ? [
          ['B', 'Bold — WhatsApp reads *stars*', () => wrap('*', '*')],
          ['I', 'Italic — WhatsApp reads _underscores_', () => wrap('_', '_')],
          ['S', 'Strikethrough — WhatsApp reads ~tildes~', () => wrap('~', '~')],
          ['Link', 'Link', () => { const u = askLink(); if (u) wrap(u, ''); }],
          ['•', 'Bulleted list', () => wrap('\n• ', '')],
          ['✕', 'Strip markers', () => onBody(String(body || '').replace(/[*_~]/g, ''))]
        ]
        : [];

  const hint = visual
    ? 'Select text and click — it applies here as you type.'
    : isEmail
      ? 'Source mode. The tag wraps whatever is selected.'
      : 'WhatsApp renders these markers when the message arrives.';

  return (
    <>
      {isEmail ? (
        <div className="edtabs">
          <button type="button" className={visual ? 'on' : ''} onClick={() => onHtml(false)}>
            <Icon name="edit" />Visual
          </button>
          <button type="button" className={html ? 'on' : ''} onClick={() => onHtml(true)}>
            <Icon name="file" />HTML
          </button>
        </div>
      ) : null}

      {tools.length ? (
        <div className="edbar">
          {tools.map(([label, title, run]) => (
            <button
              type="button"
              key={label}
              title={title}
              onClick={run}
              className={`mk mk-${label.toLowerCase().replace(/[^a-z]/g, '') || 'x'}`}
            >
              {label}
            </button>
          ))}
          <span>{hint}</span>
        </div>
      ) : null}

      {isEmail || isWhatsapp ? (
        <div className="edins">
          {isEmail ? (
            <button
              type="button"
              className="ins img"
              onClick={() => imageRef.current && imageRef.current.click()}
              disabled={uploading}
              title="Add a picture from this computer"
            >
              {uploading ? <><Spinner />Uploading…</> : <><Icon name="grid" />Image</>}
            </button>
          ) : null}

          <button
            type="button"
            className="ins sig"
            onClick={addSignature}
            disabled={!String(signature || '').trim()}
            title={String(signature || '').trim()
              ? 'Add your signature'
              : 'Set a signature in Settings → Profile first'}
          >
            <Icon name="edit" />Signature
          </button>

          {isEmail ? (
            <button
              type="button"
              className="ins att"
              disabled
              title="Attachments are not stored yet — link to the file instead"
            >
              <Icon name="up" />Attach
            </button>
          ) : null}

          <span className="edinshint">
            {isEmail
              ? 'A picture is sent inside the message, so it arrives without a public link.'
              : 'WhatsApp carries text; send a file from the chat itself.'}
          </span>

          <input
            ref={imageRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            hidden
            onChange={e => { addImage(e.target.files && e.target.files[0]); e.target.value = ''; }}
          />
        </div>
      ) : null}

      {/*
        * Merge fields, inserted where the cursor is rather than appended.
        *
        * Five of them only mean anything for somebody on the client ledger -
        * a plan, an amount owed, days overdue. A campaign to the marketing
        * list has none of that, and those fields come out empty, leaving a
        * sentence like "your  plan renews in  days". So they are marked, and
        * saying which is which here costs nothing and saves finding out from
        * a message that has already gone.
        */}
      <div className="edmerge">
        {MERGE.map(([token, description]) => {
          const ledger = LEDGER_MERGE.includes(token);
          return (
            <button
              type="button"
              key={token}
              className={`mfld${ledger ? ' ledger' : ''}`}
              title={ledger
                ? `${description} — empty unless the recipient is on the client ledger`
                : description}
              onClick={() => insert(token)}
            >
              {token}
            </button>
          );
        })}
        <span>
          Click to drop one in. The greyed ones are filled from the client ledger, so they
          come out empty for a marketing contact.
        </span>
      </div>

      {visual ? (
        <div
          ref={liveRef}
          className="livebox"
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label="Message"
          data-empty="Hello {{first}}, …"
          onInput={publish}
          onBlur={publish}
        />
      ) : (
        <textarea
          ref={areaRef}
          style={{ minHeight: 160, fontFamily: isEmail ? 'ui-monospace, Consolas, monospace' : undefined }}
          value={body || ''}
          placeholder={isEmail ? '<p>Hello {{first}},</p>' : 'Hello {{first}}, …'}
          onChange={e => onBody(e.target.value)}
        />
      )}
    </>
  );
}
